<template>
  <modal :name="'reorder-list' + orderId " :width="720">
    <div>
      <header class="modal-header center-md center-xs py15 px65 h1 sans-serif weight-700 bg-cl-secondary flex">
        <span>{{ $t("Re-order")}}</span>
        <i
          slot="close"
          class="modal-close material-icons p15 cl-bg-tertiary"
          @click="close"
          data-testid="closeModalButton"
        >
          close
        </i>
      </header>
      <div class="modal-content p20 pt0">
        <bounce-loader style="margin-bottom: 40px" v-if="loader && products.notAvailable && products.available"/>
        <p class="message-for-price">{{$t('Prices for some items are subject to change')}}</p>
        <template v-if="products.notAvailable !== undefined && products.notAvailable.length > 0">
          <h3 class="title-slider">{{ $t('The following items will not be added as they are not available')}}</h3>
          <div>
            <order-slider
              :config="config"
              :items="products.notAvailable"
              :slidesPage="slidesPage"
              :orderId="orderId"
            />
          </div>
        </template>
        <template v-if="products.available !== undefined && products.available.length > 0">
          <h3 class="title-slider">{{ $t('The following items will be added')}}</h3>
          <div>
            <order-slider
              :config="config"
              :items="products.available"
              :slidesPage="slidesPage"
              :orderId="orderId"
            />
          </div>
        </template>
      </div>
      <footer class="flex" v-if="isCheckoutAllowed">
        <div class="pb20 block-button pt0 col-md-6">
          <div
            class="button-continue-shop pointer"
            :class="{ 'is-disabled': !enabledPartner }"
            @click.prevent="remakeOrder(products.available, false)"
          >{{ $t('Add to cart')}}</div>
        </div>

        <div class="pb20 pt0 col-md-6">
          <p class="messageMinOrder" v-if="!enabledPartner && products.available && products.available.length">{{ unavailablePartnerMessage }}</p>
          <p class="messageMinOrder" v-else-if="isCheckoutDisabled && products.available && products.available.length">{{ minOrderNotReachedMessage }}</p>
          <p class="messageMinOrder" v-else-if="enabledPartner && !isCheckoutDisabled && products.available && products.available.length">
            {{$t('Minimal order amount is ')}}
            <span v-if="order.parnter">{{order.parnter.minimal_order_amount | price }}</span>
          </p>
          <div
            class="button-remake pointer"
            :class="{ 'is-disabled': !enabledPartner || isCheckoutDisabled || !minPriceOrder }"
            @click.prevent="remakeOrder(products.available, true)"
          >{{ $t('Confirm Order') }}</div>
        </div>
      </footer>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex'
import CartPartnerEligibility, { isPartnerEnabled } from 'theme/mixins/cartPartnerEligibility'

const Modal = () => import(/* webpackChunkName: "vsf-modal" */ 'theme/components/core/Modal');
const OrderSlider = () => import(/* webpackChunkName: "vsf-order-slider" */ 'theme/components/core/blocks/MyAccount/OrderSlider');
const BounceLoader = () => import(/* webpackChunkName: "vsf-bounce-loader" */ 'theme/components/theme/blocks/Home/Preloaders/BounceLoader');

export default {
  name: 'ReorderList',
  mixins: [CartPartnerEligibility],
  components: {
    Modal,
    OrderSlider,
    BounceLoader
  },
  props: {
    orderId: {
      type: Number,
      required: true
    },
    products: {
      type: Object,
      required: true
    },
    count: {
      type: Number,
      required: true
    },
    order: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      config : {
        'arrows': false,
        'infinite': false,
        'draggable': false,
        'cssEase': 'ease-out',
        'speed': 500,
        'slidesToShow': 4,
        'slidesToScroll': 4,
        perPage: 3,
        autoScaleSlider:true,
        'responsive': [
          {
            'breakpoint': 1024,
            'settings': {
              'slidesToShow': 4,
              'slidesToScroll': 4,
              'arrows': false
            }
          },
          {
            'breakpoint': 600,
            'settings': {
              'slidesToShow': 2,
              'slidesToScroll': 2,
              'arrows': false
            }
          },
          {
            'breakpoint': 480,
            'settings': {
              'slidesToShow': 2,
              'slidesToScroll': 2,
              'arrows': false
            }
          }
        ]
      },
      slidesPage : {
        desktop : 4,
        tablet: 4,
        mobileM: 2,
        mobileS:2
      }
    }
  },
  methods: {
    async remakeOrder (products, goCheckout) {
      if (!products || products.length == 0) {
        goCheckout = false
        this.notifyUnavailableProducts()
        this.close()
        return
      }
      if (!this.enabledPartner) {
        this.notifyUnavailablePartner()
        return
      }
      if (goCheckout && (this.isCheckoutDisabled || !this.minPriceOrder)) {
        this.notifyMinOrderNotReached()
        return
      }
      this.$bus.$emit('notification-progress-start', this.$t('Please wait ...'))
      await this.$store.dispatch('cart/addItems', { productsToAdd: products })
      await this.$store.dispatch('cart/load', { productsToAdd: products })
      if (products && products.length > 0) {
        this.notifyAvailableProducts()
      }
      this.$bus.$emit('modal-hide', 'reorder-list' + this.orderId)
      this.$bus.$emit('notification-progress-stop', {})
      // Redirect to the cart straight away.
      if (goCheckout) {
        this.$store.commit('ui/setMicrocart', false)
        this.$store.commit('ui/setSidebar', false)
        this.$router.push(this.localizedRoute({
          name: 'checkout',
          path: 'checkout',
          params: {
            locationCode: this.order.location_code
          }
        }))
      } else {
        this.$bus.$emit('modal-hide', 'reorder-list' + this.orderId)
      }
    },
    skipGrouped (items) {
      return items.filter((item) => {
        return !item.parent_item_id
      })
    },
    close () {
      this.$bus.$emit('modal-hide', 'reorder-list' + this.orderId)
    },
    notifyUnavailableProducts () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.$t('These ordered products are currently unavailable and cannot be added to the cart.'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyAvailableProducts () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'success',
        message: this.$t('Available products have been added to the cart!'),
        action1: { label: this.$t('OK') }
      })
    },
    notifyUnavailablePartner () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.unavailablePartnerMessage,
        action1: { label: this.$t('OK') }
      })
    },
    notifyMinOrderNotReached () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'warning',
        message: this.minOrderNotReachedMessage,
        action1: { label: this.$t('OK') }
      })
    }
  },
  computed: {
    ...mapGetters({
      loader: 'ui/getLoader',
      getGroupedByPartnerProducts: 'partners/getGroupedByPartnerProducts',
      currentLocation: 'locations/getActiveLocation',
      currentPartner: 'partners/getCurrentPartner'
    }),
    isCheckoutAllowed () {
      return (this.currentLocation && this.currentLocation.hasOwnProperty('is_checkout_allowed')) ? this.currentLocation.is_checkout_allowed : true
    },
    enabledPartner () {
      return isPartnerEnabled(this.order.partner)
    },
    unavailablePartnerMessage () {
      return this.$t('One of the partners is not available')
    },
    minOrderNotReachedMessage () {
      return this.$t('Order amount in one of partners is not enough to finish checkout')
    },
    minPriceOrder () {
      if (this.order.partner && this.order && this.products.available) {
        let subtotalOrders = 0
        this.products.available.map(product => {
          subtotalOrders += product.priceInclTax * product.qty
        });
        return subtotalOrders >= this.order.partner.minimal_order_amount
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .message-for-price {
    font-size: 14px;
    color: red;
  }
  .messageMinOrder{
    font-size: 14px;
    color: red;
  }
  .block-button{
    align-self: flex-end;
    text-align: -webkit-right;
    padding-right: 10px;
  }
  .button-continue-shop {
    padding: 10px 10px;
    background-color: #3cb6ff;
    border-radius: 10px;
    color: #ffffff;
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.05px;
    width: 65%;
  }
  .button-remake{
    padding: 10px 10px;
    background-color: #11bb2e;
    border-radius: 10px;
    color: #ffffff;
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.05px;
    width: 65%;
  }
  .button-continue-shop.is-disabled,
  .button-remake.is-disabled {
    background-color: gray;
    cursor: default;
  }
  .modal {
    font-size: 18px;
  }

  .button-full {
    min-width: 240px;
  }

  .sorry-div {
    text-align: center;
  }

  footer {
    .bg-cl-mine-shaft {
      background-color: #11bb2e;
    }
  }

  @media screen and (max-width: 525px) {
    .button-full:first-child {
      margin-bottom: 10px;
    }

    .modal-content {
      text-align: center;
    }
  }
  .modal-header{
    font-size: 16px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.05px;
    text-align: center;
    color: #111111;
    background: #ffffff;
    border: solid 1px #ebebeb;
  }
  .title-slider{
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.11px;
    color: #111111;
    text-align: left;
  }
</style>
